import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  decideApproval,
  getApproval,
  requestStepUp,
  verifySca,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { newIdempotencyKey } from '@/services/idempotency';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice, RestrictedState } from '@/components/feedback';
import { Sheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { formatDateTime, formatIban, formatSortCode } from '@/services/formatters';
import { isMockApiError } from '@/mocks/errors';

/**
 * Approval decision — WF-SMEM-07.
 *
 * The decision is the regulated act on this screen, so it carries:
 *  - the full risk and payee-verification evidence the approver needs;
 *  - a mandatory reason when rejecting;
 *  - step-up authentication before either decision takes effect;
 *  - an already-actioned state when someone else decided first;
 *  - an immutable audit event.
 *
 * AI never appears here. Nothing on this screen may approve or reject on the
 * user's behalf.
 */
type Decision = 'approve' | 'reject';

export function ApprovalDetailPage() {
  const { approvalId = '' } = useParams();
  const resolved = resolveContext();
  const toast = useToast();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [scaCode, setScaCode] = useState('');
  const [scaError, setScaError] = useState<string | null>(null);
  const [alreadyActioned, setAlreadyActioned] = useState(false);
  const idempotencyKey = useRef<string>(newIdempotencyKey('apr'));

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const item = useAsync(async () => {
    if (!companyId) throw new Error('no session');
    return (await getApproval(companyId, approvalId)).data;
  }, [companyId, approvalId]);

  const decideMutation = useMutation(async (d: Decision, why: string | null) => {
    if (!companyId || !userId) throw new Error('no session');
    return (
      await decideApproval(companyId, userId, approvalId, d, why, idempotencyKey.current)
    ).data;
  });

  if (!resolved) return null;

  const canDecide = resolveCapability(resolved.entitlements, CAPABILITIES.approvalsDecide).allowed;

  if (!canDecide) {
    return (
      <>
        <MobileHeader title="Approval" backTo="/mobile/approvals" backLabel="Approvals" />
        <RestrictedState
          title="Not available"
          message="You are not an approver for this company. If you think that is wrong, ask an administrator to check your access."
        />
      </>
    );
  }

  if (item.status === 'loading') {
    return (
      <>
        <MobileHeader title="Approval" backTo="/mobile/approvals" backLabel="Approvals" />
        <LoadingRegion label="Loading approval">
          <div className="tm-stack">
            <CardSkeleton height={160} />
            <CardSkeleton height={220} />
          </div>
        </LoadingRegion>
      </>
    );
  }

  if (item.status === 'error') {
    return (
      <>
        <MobileHeader title="Approval" backTo="/mobile/approvals" backLabel="Approvals" />
        <ErrorState error={item.error} onRetry={item.reload} title="We could not load this request" />
      </>
    );
  }

  const { approval, payment, beneficiary, sourceAccount } = item.data;
  const settled = approval.status !== 'pending' || alreadyActioned;
  const verification = beneficiary?.verificationResult ?? 'unavailable';
  const insufficient = sourceAccount ? payment.amount > sourceAccount.availableBalance : false;

  const startDecision = (d: Decision) => {
    setDecision(d);
    setReason('');
    setReasonError(null);
    setScaCode('');
    setScaError(null);
    setChallengeId(null);
  };

  const beginStepUp = async () => {
    if (!userId || !decision) return;
    if (decision === 'reject' && !reason.trim()) {
      setReasonError('Give a reason so the person who raised this knows what to change.');
      return;
    }
    const response = await requestStepUp(userId, `approval:${approvalId}`);
    setChallengeId(response.data.challengeId);
  };

  const confirmDecision = async () => {
    if (!challengeId || !decision) return;
    setScaError(null);
    try {
      await verifySca(challengeId, scaCode);
    } catch (e: unknown) {
      setScaError(isMockApiError(e) ? e.message : 'That code was not correct.');
      setScaCode('');
      return;
    }
    const outcome = await decideMutation.run(decision, decision === 'reject' ? reason : null);
    if (outcome.ok) {
      item.setData(outcome.value);
      setDecision(null);
      toast.show({
        message: decision === 'approve' ? 'Payment approved and sent.' : 'Payment rejected.',
      });
    } else if (outcome.error) {
      if (isMockApiError(outcome.error) && outcome.error.code === 'ALREADY_ACTIONED') {
        setAlreadyActioned(true);
        setDecision(null);
      } else {
        setScaError(
          isMockApiError(outcome.error) ? outcome.error.message : 'We could not record your decision.',
        );
      }
    }
  };

  return (
    <>
      <MobileHeader title="Approval" backTo="/mobile/approvals" backLabel="Approvals" />

      <div className="tm-stack">
        <div className="tm-card" style={{ textAlign: 'center' }}>
          <p className="tm-eyebrow">Approve a payment to</p>
          <h2 className="tm-page-title" style={{ marginBlockEnd: 8 }}>
            {beneficiary?.name ?? 'Payee'}
          </h2>
          <Amount minorUnits={payment.amount} currency={payment.currency} size="hero" />
          <div style={{ marginBlockStart: 10 }}>
            <StatusBadge status={payment.status} />
          </div>
        </div>

        {settled ? (
          <Notice tone="info" title="Already decided">
            This request has already been {approval.status === 'rejected' ? 'rejected' : 'approved'}
            {alreadyActioned ? ' by someone else' : ''}. No further action is needed.
            {approval.rejectionReason ? ` Reason given: ${approval.rejectionReason}` : ''}
          </Notice>
        ) : null}

        {/* Risk and verification evidence. */}
        <section aria-labelledby="apr-risk">
          <SectionHeader title="What to check" id="apr-risk" />
          <div className="tm-stack tm-stack--tight">
            <Notice
              tone={
                verification === 'match' ? 'success' : verification === 'no-match' ? 'danger' : 'warning'
              }
              title={
                verification === 'match'
                  ? 'Payee name matches'
                  : verification === 'no-match'
                    ? 'Payee name does not match'
                    : verification === 'close-match'
                      ? 'Payee name is a close match only'
                      : 'Payee name could not be checked'
              }
            >
              {verification === 'match'
                ? 'The receiving bank confirmed the account holder name.'
                : verification === 'no-match'
                  ? 'The receiving bank holds a different name for this account. Money sent to the wrong account may not be recoverable.'
                  : verification === 'close-match'
                    ? 'The receiving bank holds a similar but not identical name.'
                    : 'Account name checking is not available for this destination.'}
              {payment.payeeVerification?.checkedAt
                ? ` Checked ${formatDateTime(payment.payeeVerification.checkedAt)}.`
                : ''}
            </Notice>

            {payment.payeeVerification?.proceededDespiteWarning ? (
              <Notice tone="warning" title="The creator continued past this warning">
                Whoever raised this payment saw the payee-check result and chose to proceed.
              </Notice>
            ) : null}

            {insufficient ? (
              <Notice tone="warning" title="Not enough available balance">
                The source account does not currently hold enough available balance for this
                payment.
              </Notice>
            ) : null}

            {approval.thresholdAmount ? (
              <Notice tone="info" title="Why this needs approval">
                It is{' '}
                <Amount minorUnits={approval.thresholdAmount} currency={payment.currency} /> or
                more, which exceeds the approval threshold for this company.
              </Notice>
            ) : null}
          </div>
        </section>

        <section aria-labelledby="apr-details" className="tm-card">
          <SectionHeader title="Payment details" id="apr-details" />
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'From', value: sourceAccount?.displayName ?? '—' },
                {
                  label: 'Available',
                  value: sourceAccount ? (
                    <Amount
                      minorUnits={sourceAccount.availableBalance}
                      currency={sourceAccount.currency}
                    />
                  ) : (
                    '—'
                  ),
                },
                { label: 'To', value: beneficiary?.name ?? '—' },
                ...(beneficiary?.accountNumber
                  ? [
                      {
                        label: 'Account',
                        value: `${formatSortCode(beneficiary.sortCode ?? '')} · ${beneficiary.accountNumber}`,
                      },
                    ]
                  : []),
                ...(beneficiary?.iban
                  ? [{ label: 'IBAN', value: formatIban(beneficiary.iban) }]
                  : []),
                { label: 'Reference', value: payment.reference },
                { label: 'Raised', value: formatDateTime(payment.createdAt) },
                { label: 'Approvals needed', value: String(approval.requiredApprovals) },
              ]}
            />
          </div>
        </section>

        {!settled ? (
          <div className="tm-stack tm-stack--tight">
            <button
              type="button"
              className="tm-btn tm-btn--block"
              onClick={() => startDecision('approve')}
            >
              Approve payment
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--danger tm-btn--block"
              onClick={() => startDecision('reject')}
            >
              Reject payment
            </button>
            <p className="tm-hint" style={{ textAlign: 'center' }}>
              Either decision needs a security code and is recorded in the audit trail.
            </p>
          </div>
        ) : (
          <Link to="/mobile/approvals" className="tm-btn tm-btn--secondary tm-btn--block">
            Back to approvals
          </Link>
        )}
      </div>

      {/* Decision sheet: reason, then step-up. */}
      <Sheet
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision === 'approve' ? 'Approve this payment?' : 'Reject this payment?'}
      >
        {challengeId === null ? (
          <div className="tm-stack">
            <p className="tm-muted">
              {decision === 'approve' ? (
                <>
                  Approving sends{' '}
                  <Amount minorUnits={payment.amount} currency={payment.currency} /> to{' '}
                  {beneficiary?.name ?? 'the payee'}. This cannot be undone once the money leaves.
                </>
              ) : (
                <>Rejecting cancels this payment. No money will move.</>
              )}
            </p>

            {decision === 'reject' ? (
              <div className="tm-field">
                <label className="tm-label" htmlFor="apr-reason">
                  Reason for rejecting
                </label>
                <textarea
                  id="apr-reason"
                  className="tm-textarea"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  aria-invalid={reasonError ? true : undefined}
                  aria-describedby={reasonError ? 'apr-reason-error' : undefined}
                />
                <div className="tm-field__error-slot">
                  {reasonError ? (
                    <span className="tm-field__error" id="apr-reason-error">
                      {reasonError}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            <button
              type="button"
              className={`tm-btn tm-btn--block${decision === 'reject' ? ' tm-btn--danger' : ''}`}
              onClick={() => void beginStepUp()}
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="tm-stack">
            <p className="tm-muted">
              Enter the 6-digit code from your authenticator app to record this decision.
            </p>
            <div className="tm-field">
              <label className="tm-label" htmlFor="apr-sca">
                Authentication code
              </label>
              <input
                id="apr-sca"
                className="tm-input"
                inputMode="numeric"
                maxLength={6}
                value={scaCode}
                onChange={(e) => setScaCode(e.target.value.replace(/\D/g, ''))}
                aria-invalid={scaError ? true : undefined}
                aria-describedby={scaError ? 'apr-sca-error' : 'apr-sca-hint'}
                style={{ letterSpacing: '0.4em' }}
              />
              <div className="tm-field__error-slot">
                {scaError ? (
                  <span className="tm-field__error" id="apr-sca-error">
                    {scaError}
                  </span>
                ) : (
                  <span className="tm-hint" id="apr-sca-hint">
                    Demo code: 481920
                  </span>
                )}
              </div>
            </div>
            {/* Same guard as the card reveal: never live before the challenge
                exists, or a fast tap is silently dropped. */}
            <button
              type="button"
              className={`tm-btn tm-btn--block${decision === 'reject' ? ' tm-btn--danger' : ''}`}
              disabled={scaCode.length !== 6 || decideMutation.pending || challengeId === null}
              onClick={() => void confirmDecision()}
            >
              {decideMutation.pending
                ? 'Recording…'
                : decision === 'approve'
                  ? 'Approve and send'
                  : 'Reject payment'}
            </button>
          </div>
        )}
      </Sheet>
    </>
  );
}
